import axios from 'axios';

// Base URL de l'API AlQuran Cloud.
//
// En production la valeur par défaut est utilisée telle quelle : le build ne
// définit pas REACT_APP_QURAN_API_URL, donc rien ne change. En développement,
// `.env.development.local` la pointe sur un chemin relatif servi par le proxy
// de `src/setupProxy.js`, car api.alquran.cloud ne renvoie pas d'en-tête
// Access-Control-Allow-Origin pour http://localhost:3000.
const API_BASE_URL = process.env.REACT_APP_QURAN_API_URL || 'https://api.alquran.cloud/v1';

// Fonction utilitaire pour mélanger un tableau
function shuffleArray(array) {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
}

// Bornes maximales par type d'unité de révision
export const RANGE_LIMITS = {
  page: 604,
  juz: 30,
  hizb: 60,
  surah: 114
};

// Cache mémoire des appels API (une même page/juz/sourate n'est téléchargée qu'une fois)
const requestCache = new Map();

async function cachedGet(url) {
  if (requestCache.has(url)) {
    return requestCache.get(url);
  }
  const promise = axios.get(url).then(response => response.data.data);
  requestCache.set(url, promise);
  try {
    return await promise;
  } catch (error) {
    requestCache.delete(url);
    throw error;
  }
}

// Petite pause entre deux tentatives réseau
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Construit un objet verset normalisé à partir d'une ayah déjà chargée :
// le texte arabe et les métadonnées sont garantis, sans appel réseau.
function buildVerseFromAyah(ayah) {
  const page = ayah.page || null;
  return {
    surah: ayah.surah?.number ?? ayah.surah,
    numberInSurah: ayah.numberInSurah,
    text: ayah.text,
    arabic: ayah.text,
    translation: '',
    hizb: Math.ceil((ayah.hizbQuarter || 1) / 4),
    hizbQuarter: ayah.hizbQuarter,
    juz: ayah.juz,
    page,
    pageImageURL: page ? `https://tanzil.net/res/img/mushaf/png-600/page${String(page).padStart(3, '0')}.png` : null
  };
}

// Récupère le texte uthmani et la traduction d'un verset, avec nouvelles tentatives
// en cas d'échec transitoire (l'API limite parfois le débit).
async function fetchVerseDetails(surahNumber, verseNumber, translationEdition, retries = 2) {
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const [arabicData, translationData, audioData] = await Promise.all([
        cachedGet(`${API_BASE_URL}/ayah/${surahNumber}:${verseNumber}/quran-uthmani`),
        cachedGet(`${API_BASE_URL}/ayah/${surahNumber}:${verseNumber}/${translationEdition}`),
        cachedGet(`${API_BASE_URL}/ayah/${surahNumber}:${verseNumber}/ar.alafasy`)
      ]);
      return {
        arabic: arabicData?.text || '',
        translation: translationData?.text || '',
        audioUrl: audioData?.audio || ''
      };
    } catch (error) {
      if (attempt === retries) {
        throw error;
      }
      await delay(500 * (attempt + 1));
    }
  }
  return { arabic: '', translation: '' };
}

// Construit la liste ordonnée des unités (pages, juz, hizbs, sourates) d'une plage
function buildOrderedRange(start, end, reverseMode = false) {
  const from = Math.min(start, end);
  const to = Math.max(start, end);
  const units = [];
  for (let value = from; value <= to; value++) {
    units.push(value);
  }
  return reverseMode ? units.reverse() : units;
}

/**
 * Répartit `questionCount` questions sur une liste d'unités ordonnées.
 *
 * - Si questionCount <= nombre d'unités : la plage est découpée en `questionCount`
 *   segments contigus de taille quasi égale et une unité est tirée au hasard dans
 *   chaque segment. Cela garantit une couverture complète de la plage, sans grappes
 *   ni trous (contrairement à un pas fixe ou à un tirage purement aléatoire).
 * - Si questionCount > nombre d'unités : chaque unité reçoit un quota de base et les
 *   questions restantes sont attribuées à des unités régulièrement espacées.
 *
 * L'ordre de lecture de la plage est toujours préservé.
 */
function distributeQuestions(units, questionCount) {
  const total = units.length;
  if (total === 0 || questionCount <= 0) {
    return [];
  }

  if (questionCount <= total) {
    const picks = [];
    for (let i = 0; i < questionCount; i++) {
      const segmentStart = Math.floor((i * total) / questionCount);
      const segmentEnd = Math.floor(((i + 1) * total) / questionCount) - 1;
      const span = Math.max(1, segmentEnd - segmentStart + 1);
      picks.push(units[segmentStart + Math.floor(Math.random() * span)]);
    }
    return picks;
  }

  const base = Math.floor(questionCount / total);
  const remainder = questionCount % total;
  const bonusIndexes = new Set();
  for (let i = 0; i < remainder; i++) {
    bonusIndexes.add(Math.floor((i * total) / remainder));
  }

  const distribution = [];
  units.forEach((unit, index) => {
    const quota = base + (bonusIndexes.has(index) ? 1 : 0);
    for (let i = 0; i < quota; i++) {
      distribution.push(unit);
    }
  });
  return distribution;
}

// Sélectionne un ayah non encore utilisé dans un ensemble donné
function pickUnusedAyah(ayahs, usedVerseKeys) {
  if (!ayahs || ayahs.length === 0) {
    return null;
  }
  const pool = shuffleArray(ayahs);
  const fresh = pool.find(
    ayah => !usedVerseKeys.has(`${ayah.surah.number}:${ayah.numberInSurah}`)
  );
  const chosen = fresh || pool[0];
  usedVerseKeys.add(`${chosen.surah.number}:${chosen.numberInSurah}`);
  return chosen;
}

// Récupère les ayahs appartenant à un hizb précis (2 hizbs par juz)
async function getAyahsForHizb(hizb) {
  const juz = Math.ceil(hizb / 2);
  const juzData = await cachedGet(`${API_BASE_URL}/juz/${juz}/quran-simple`);
  return (juzData.ayahs || []).filter(ayah => Math.ceil(ayah.hizbQuarter / 4) === hizb);
}

// Récupère les ayahs d'une unité (page, juz, hizb ou sourate)
async function getAyahsForUnit(unitType, unitValue) {
  if (unitType === 'hizb') {
    return getAyahsForHizb(unitValue);
  }
  const endpoint = unitType === 'surah' ? 'surah' : unitType; // page | juz | surah
  const data = await cachedGet(`${API_BASE_URL}/${endpoint}/${unitValue}/quran-simple`);
  return data.ayahs || [];
}

// Comparateur de position dans le Coran (numéro absolu de verset)
function compareVersePosition(a, b, reverseMode) {
  const left = (a.surah || 0) * 1000 + (a.numberInSurah || 0);
  const right = (b.surah || 0) * 1000 + (b.numberInSurah || 0);
  return reverseMode ? right - left : left - right;
}

// Exécute un traitement asynchrone sur une liste avec une concurrence limitée
async function mapWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let cursor = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await worker(items[index], index);
    }
  });
  await Promise.all(runners);
  return results;
}

/**
 * Construit un examen à partir d'une plage d'unités (page, juz, hizb ou sourate).
 * Le déroulement se fait en trois phases pour limiter le temps d'attente :
 *   1. chargement parallèle (concurrence limitée) des unités distinctes,
 *   2. sélection locale des versets, sans doublon, avec repli sur l'unité voisine,
 *   3. récupération parallèle des textes et traductions.
 */
async function buildExamFromUnits({
  unitType,
  units,
  questionCount,
  translationEdition = 'fr.hamidullah',
  reverseMode = false,
  rangeLabel = null
}) {
  const plannedUnits = distributeQuestions(units, questionCount);
  if (plannedUnits.length === 0) {
    return [];
  }

  // Phase 1 : chargement des unités distinctes
  const uniqueUnits = [...new Set(plannedUnits)];
  const ayahsByUnit = new Map();
  await mapWithConcurrency(uniqueUnits, 4, async unitValue => {
    try {
      ayahsByUnit.set(unitValue, await getAyahsForUnit(unitType, unitValue));
    } catch (error) {
      console.error(`[Examen] Chargement impossible pour ${unitType} ${unitValue}:`, error);
      ayahsByUnit.set(unitValue, []);
    }
  });

  // Phase 2 : sélection des versets, sans doublon
  const usedVerseKeys = new Set();
  const selections = [];
  plannedUnits.forEach(unitValue => {
    let effectiveUnit = unitValue;
    let ayahs = ayahsByUnit.get(unitValue) || [];

    if (ayahs.length === 0) {
      const neighbour = uniqueUnits
        .filter(candidate => (ayahsByUnit.get(candidate) || []).length > 0)
        .sort((a, b) => Math.abs(a - unitValue) - Math.abs(b - unitValue))[0];
      if (neighbour === undefined) {
        return;
      }
      effectiveUnit = neighbour;
      ayahs = ayahsByUnit.get(neighbour);
    }

    const ayah = pickUnusedAyah(ayahs, usedVerseKeys);
    if (ayah) {
      selections.push({
        ayah,
        unitValue: effectiveUnit,
        isAlternative: effectiveUnit !== unitValue
      });
    }
  });

  // Phase 3 : construction des questions. Le texte arabe est garanti par les ayahs
  // déjà chargées (phase 1) : même si le chargement détaillé échoue malgré les
  // nouvelles tentatives, la question reste affichable au lieu d'être vide.
  const fetched = await mapWithConcurrency(selections, 4, async ({ ayah, unitValue, isAlternative }) => {
    try {
      const verse = buildVerseFromAyah(ayah);

      try {
        const details = await fetchVerseDetails(ayah.surah.number, ayah.numberInSurah, translationEdition);
        verse.text = details.arabic || verse.text;
        verse.arabic = details.arabic || verse.arabic;
        verse.translation = details.translation || 'Traduction indisponible.';
        verse.audioUrl = details.audioUrl || '';
      } catch (detailError) {
        console.warn(`[Examen] Détails indisponibles pour ${ayah.surah.number}:${ayah.numberInSurah}, affichage du texte local.`, detailError);
        verse.translation = 'Traduction indisponible (erreur réseau).';
      }

      verse.examInfo = {
        selectionType: unitType,
        selectedValue: unitValue,
        selectionRange: rangeLabel,
        isAlternative,
        ...(unitType === 'hizb' ? { hizbTested: unitValue } : {})
      };
      return verse;
    } catch (error) {
      console.error(`[Examen] Verset ${ayah.surah.number}:${ayah.numberInSurah} indisponible:`, error);
      return null;
    }
  });

  const examVerses = fetched.filter(Boolean);

  // Ordre de lecture : unité puis position du verset dans le Coran
  examVerses.sort((a, b) => {
    const unitDiff = reverseMode
      ? b.examInfo.selectedValue - a.examInfo.selectedValue
      : a.examInfo.selectedValue - b.examInfo.selectedValue;
    return unitDiff !== 0 ? unitDiff : compareVersePosition(a, b, reverseMode);
  });

  examVerses.forEach((verse, index) => {
    verse.examInfo.questionNumber = index + 1;
    verse.examInfo.totalQuestions = examVerses.length;
  });

  return examVerses;
}

// URL pour les images des pages du Coran - supprimée car non utilisée
// La méthode getQuranPageImage utilise des images locales à la place

// Service pour interagir avec l'API du Coran
const QuranAPI = {
  getSurahList: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/meta`); // API_BASE_URL est défini globalement dans ce fichier
      if (response.data && response.data.data && response.data.data.surahs && response.data.data.surahs.references) {
        return { data: response.data.data.surahs.references };
      } else {
        console.error('Structure de réponse inattendue de /meta:', response.data);
        return { error: true, message: 'Structure de réponse inattendue de /meta' };
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de la liste des sourates (/meta):', error);
      return { error: true, details: error, message: 'Erreur API /meta' };
    }
  },

  // Récupérer une sourate complète
  getSurah: async (surahNumber, edition = 'quran-simple') => {
    try {
      const response = await axios.get(`${API_BASE_URL}/surah/${surahNumber}/${edition}`);
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération de la sourate:', error);
      throw error;
    }
  },

  // Récupérer un verset spécifique
  getVerse: async (surahNumber, verseNumber, edition = 'quran-simple') => {
    try {
      const response = await axios.get(`${API_BASE_URL}/ayah/${surahNumber}:${verseNumber}/${edition}`);
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du verset:', error);
      throw error;
    }
  },

  // Récupérer un verset avec traduction
  getVerseWithTranslation: async (surahNumber, verseNumber, translationEdition = 'fr.hamidullah') => {
    try {
      // Récupérer le verset en arabe
      const arabicVersePromise = QuranAPI.getVerse(surahNumber, verseNumber, 'quran-simple');

      // Récupérer la traduction et l'audio du verset
      const translationPromise = QuranAPI.getVerse(surahNumber, verseNumber, translationEdition);
      const audioPromise = QuranAPI.getVerse(surahNumber, verseNumber, 'ar.alafasy');

      // Attendre les trois requêtes
      const [arabicVerseResponse, translationResponse, audioResponse] = await Promise.all([
        arabicVersePromise.catch(e => ({ error: true, details: e, source: 'arabicVersePromise' })),
        translationPromise.catch(e => ({ error: true, details: e, source: 'translationPromise' })),
        audioPromise.catch(e => ({ error: true, details: e, source: 'audioPromise' }))
      ]);

      // Vérifier les erreurs ou les données incomplètes
      if (arabicVerseResponse.error || !arabicVerseResponse?.surah?.number || arabicVerseResponse.numberInSurah === undefined || !arabicVerseResponse.text ||
          translationResponse.error || !translationResponse?.text) {
        console.warn(`[QuranAPI] Données incomplètes ou erreur API pour ${surahNumber}:${verseNumber}.`);
        if(arabicVerseResponse.error) console.warn('Erreur (verset arabe):', arabicVerseResponse.details);
        if(translationResponse.error) console.warn('Erreur (traduction):', translationResponse.details);

        return {
          text: `Données API défaillantes pour ${surahNumber}:${verseNumber}. Veuillez réessayer.`,
          surah: surahNumber, // Numéro de sourate demandé
          surahName: `Sourate ${surahNumber}`,
          numberInSurah: verseNumber, // Numéro de verset demandé, crucial pour le tri
          hizb: null,
          edition: translationEdition,
          originalText: 'Erreur de chargement du texte arabe.',
          translation: 'Erreur de chargement de la traduction.',
          error: true,
          page: null,
          pageImageURL: '/quran-placeholder.png', // Image par défaut
          audioUrl: ''
        };
      }

      // Si les données sont valides, procéder comme avant
      const arabicVerse = arabicVerseResponse;
      const translation = translationResponse;

      // Calcul précis du hizb à partir du hizbQuarter
      const hizb = Math.ceil(arabicVerse.hizbQuarter / 4);

      return {
        surah: arabicVerse.surah.number,
        // Renommer 'ayah' en 'numberInSurah' pour la cohérence avec le reste de l'application
        numberInSurah: arabicVerse.numberInSurah,
        text: arabicVerse.text, // Texte arabe pour la propriété 'text' principale
        arabic: arabicVerse.text, // Garder 'arabic' pour compatibilité si utilisé ailleurs
        translation: translation.text,
        transliteration: '', // La translittération n'est pas toujours disponible
        hizb: hizb,
        hizbProgress: ((arabicVerse.hizbQuarter - 1) % 4) * 25,
        frequency: 5, // Valeur par défaut
        page: arabicVerse.page,
        pageImageURL: `https://tanzil.net/res/img/mushaf/png-600/page${String(arabicVerse.page).padStart(3,'0')}.png`,
        audioUrl: audioResponse?.audio || ''
      };
    } catch (error) {
      console.error('Erreur lors de la récupération du verset avec traduction:', error);
      throw error;
    }
  },

  // Récupérer plusieurs versets consécutifs de manière optimisée
  getConsecutiveVerses: async (surahNumber, startVerse, count, translationEdition = 'fr.hamidullah') => {
    try {
      const verses = [];
      let currentSurah = surahNumber;
      let currentStartVerse = startVerse;
      let remainingCount = count;

      while (remainingCount > 0 && currentSurah <= 114) {
        // Récupérer les informations de la sourate courante
        const surah = await QuranAPI.getSurah(currentSurah);
        const totalVerses = surah.numberOfAyahs;

        // Déterminer combien de versets nous pouvons récupérer dans cette sourate
        const versesInCurrentSurah = Math.min(remainingCount, totalVerses - currentStartVerse + 1);

        if (versesInCurrentSurah <= 0) {
          // Passer à la sourate suivante si nous avons déjà dépassé tous les versets
          currentSurah++;
          currentStartVerse = 1;
          continue;
        }

        // Récupérer la sourate entière avec traduction
        try {
          // Récupérer le texte arabe
          const arabicResponse = await axios.get(`${API_BASE_URL}/surah/${currentSurah}/quran-simple`);
          const arabicSurah = arabicResponse.data.data;

          // Récupérer la traduction
          const translationResponse = await axios.get(`${API_BASE_URL}/surah/${currentSurah}/${translationEdition}`);
          const translationSurah = translationResponse.data.data;

          // Extraire les versets dont nous avons besoin
          for (let i = 0; i < versesInCurrentSurah; i++) {
            const verseIndex = currentStartVerse - 1 + i;

            if (verseIndex < arabicSurah.ayahs.length) {
              const arabicVerse = arabicSurah.ayahs[verseIndex];
              const translationVerse = translationSurah.ayahs[verseIndex];

              verses.push({
                surah: currentSurah,
                ayah: arabicVerse.numberInSurah,
                arabic: arabicVerse.text,
                translation: translationVerse.text,
                transliteration: '', // La translittération n'est pas disponible par défaut
                hizb: Math.ceil(arabicVerse.juz / 2), // Approximation du hizb basée sur le juz
                frequency: 5, // Valeur par défaut
                page: arabicVerse.page, // Numéro de page dans le mushaf
                pageImageURL: `https://tanzil.net/res/img/mushaf/png-600/page${String(arabicVerse.page).padStart(3,'0')}.png`
              });
            }
          }

          // Mettre à jour les compteurs
          remainingCount -= versesInCurrentSurah;
          currentSurah++;
          currentStartVerse = 1;

        } catch (err) {
          console.error(`Erreur lors de la récupération de la sourate ${currentSurah}:`, err);
          // Tenter de récupérer les versets un par un en cas d'échec de l'approche par sourate
          for (let i = 0; i < versesInCurrentSurah; i++) {
            try {
              const verse = await QuranAPI.getVerseWithTranslation(
                currentSurah,
                currentStartVerse + i,
                translationEdition
              );
              verses.push(verse);
            } catch (verseError) {
              console.error(`Impossible de récupérer le verset ${currentSurah}:${currentStartVerse + i}`, verseError);
              // Continuer avec le verset suivant même en cas d'erreur
            }
          }

          // Mettre à jour les compteurs même en cas d'erreur
          remainingCount -= versesInCurrentSurah;
          currentSurah++;
          currentStartVerse = 1;
        }
      }

      return verses;
    } catch (error) {
      console.error('Erreur lors de la récupération des versets consécutifs:', error);
      throw error;
    }
  },

  // Récupérer un verset aléatoire
  getRandomVerse: async (translationEdition = 'fr.hamidullah') => {
    try {
      // Le Coran contient 114 sourates
      const randomSurah = Math.floor(Math.random() * 114) + 1;

      // Récupérer la sourate pour connaître le nombre de versets
      const surah = await QuranAPI.getSurah(randomSurah);
      const totalVerses = surah.numberOfAyahs;

      // Générer un numéro de verset aléatoire
      const randomVerse = Math.floor(Math.random() * totalVerses) + 1;

      // Récupérer le verset aléatoire avec sa traduction
      return await QuranAPI.getVerseWithTranslation(randomSurah, randomVerse, translationEdition);
    } catch (error) {
      console.error('Erreur lors de la récupération d\'un verset aléatoire:', error);
      throw error;
    }
  },

  // Récupérer un verset aléatoire en fonction du niveau (Hizb)
  // Le paramètre reverseMode permet d'inverser la sélection (depuis la fin du Coran vers le début)
  getRandomVerseByLevel: async (level, translationEdition = 'fr.hamidullah', reverseMode = false) => {
    try {
      // Nombre maximum de tentatives pour trouver un verset valide
      const MAX_ATTEMPTS = 10;
      let attempt = 0;

      while (attempt < MAX_ATTEMPTS) {
        attempt++;

        // Convertir le niveau (Hizb) en Juz (approximativement)
        // Il y a 30 juz dans le Coran, et 60 hizb (2 hizb par juz)
        const totalJuz = 30; // Nombre total de juz dans le Coran
        const maxJuz = Math.ceil(level / 2); // Niveau 60 hizb = 30 juz (Coran complet)

        let randomJuz;
        if (reverseMode) {
          // Mode inversé: on prend les juz depuis la fin du Coran
          // On commence par le juz 30 et on remonte vers le début en fonction du niveau
          const minJuz = totalJuz - maxJuz + 1;
          randomJuz = minJuz + Math.floor(Math.random() * maxJuz);
          console.log(`Mode inversé: sélection aléatoire d'un juz entre ${minJuz} et ${totalJuz}`);
        } else {
          // Mode normal: on prend les juz depuis le début du Coran
          randomJuz = Math.floor(Math.random() * maxJuz) + 1;
          console.log(`Mode normal: sélection aléatoire d'un juz entre 1 et ${maxJuz}`);
        }

        // Récupérer des informations sur ce juz
        const response = await axios.get(`${API_BASE_URL}/juz/${randomJuz}/quran-simple`);
        const juzData = response.data.data;

        // Sélectionner un verset aléatoire du juz
        const ayahs = juzData.ayahs;
        const randomAyahIndex = Math.floor(Math.random() * ayahs.length);
        const randomAyah = ayahs[randomAyahIndex];

        // Récupérer le verset complet avec sa traduction
        const verse = await QuranAPI.getVerseWithTranslation(
          randomAyah.surah.number,
          randomAyah.numberInSurah,
          translationEdition
        );

        // Vérifier que le verset correspond au niveau et au mode sélectionnés
        if (reverseMode) {
          // En mode inversé, on vérifie que le hizb est supérieur à (60 - level)
          // Par exemple, si level = 60, on accepte tous les versets
          // Si level = 30, on accepte les versets des hizb 31 à 60
          const minHizb = 60 - level + 1;
          if (verse.hizb >= minHizb) {
            console.log(`Mode inversé: Verset trouvé au hizb ${verse.hizb} >= ${minHizb} (pour niveau ${level})`);
            return verse;
          } else {
            console.log(`Mode inversé: Verset ignoré: hizb ${verse.hizb} < ${minHizb} (niveau ${level}), tentative ${attempt}/${MAX_ATTEMPTS}`);
          }
        } else {
          // Mode normal: on vérifie que le hizb est inférieur ou égal au niveau sélectionné
          if (verse.hizb <= level) {
            console.log(`Mode normal: Verset trouvé au hizb ${verse.hizb} <= ${level}`);
            return verse;
          } else {
            console.log(`Mode normal: Verset ignoré: hizb ${verse.hizb} > ${level}, tentative ${attempt}/${MAX_ATTEMPTS}`);
          }
        }
      }

      // Si aucun verset valide n'est trouvé après plusieurs tentatives,
      // chercher explicitement un verset avec un niveau inférieur ou égal
      console.log(`Recherche d'un verset avec niveau <= ${level} après ${MAX_ATTEMPTS} tentatives`);

      // Approche alternative après échec des tentatives
      let safeJuz;
      if (reverseMode) {
        // En mode inversé, on prend un juz vers la fin du Coran
        const totalJuz = 30;
        const maxJuz = Math.ceil(level / 2);
        safeJuz = totalJuz - Math.floor(maxJuz / 2); // Prendre un juz médian pour maximiser les chances
      } else {
        // En mode normal, on prend un juz vers le début du Coran
        safeJuz = Math.floor(level / 2);
      }
      const response = await axios.get(`${API_BASE_URL}/juz/${safeJuz > 0 ? safeJuz : 1}/quran-simple`);
      const juzData = response.data.data;

      // Sélectionner un verset de ce juz
      const ayahs = juzData.ayahs;
      const randomAyahIndex = Math.floor(Math.random() * ayahs.length);
      const randomAyah = ayahs[randomAyahIndex];

      return await QuranAPI.getVerseWithTranslation(
        randomAyah.surah.number,
        randomAyah.numberInSurah,
        translationEdition
      );
    } catch (error) {
      console.error('Erreur lors de la récupération d\'un verset aléatoire par niveau:', error);

      // En cas d'erreur, fallback vers un verset complètement aléatoire
      return await QuranAPI.getRandomVerse(translationEdition);
    }
  },

  // Récupérer l'image d'une page spécifique du Coran
  getQuranPageImage: (pageNumber) => {
    if (!pageNumber || pageNumber < 1 || pageNumber > 604) { // Le Coran contient 604 pages
      return null;
    }

    // Utiliser les images locales du dossier public/images avec le format correct
    // Le format semble être simplement le numéro sans padding ni préfixe "page"
    return `/images/${pageNumber}.png`;
  },

  // Méthode helper pour obtenir toutes les URLs d'image possibles (pour référence)
  getAllPossibleImageUrls: (pageNumber) => {
    if (!pageNumber) return [];

    const formattedPage = String(pageNumber).padStart(3, '0');
    return [
      // Options externes (avec risque CORS)
      `https://api.quran.com/api/v4/page_images/${formattedPage}?width=800`,
      `https://qurancdn.com/images/page-images/${formattedPage}.png`,
      `https://tanzil.net/res/img/mushaf/png-600/page${formattedPage}.png`,
      // Option locale
      `/quran-placeholder.png`
    ];
  },

  // Répartition théorique des questions (utilisable par l'UI pour prévisualiser l'examen)
  planExamUnits: (unitType, startRange, endRange, questionCount, reverseMode = false) => {
    const units = buildOrderedRange(startRange, endRange, reverseMode);
    return distributeQuestions(units, questionCount);
  },

  // Examen basé sur un niveau exprimé en hizbs : la plage de hizbs est découpée
  // en autant de segments que de questions, un verset étant tiré dans chaque segment.
  getExamVerses: async (level, translationEdition = 'fr.hamidullah', reverseMode = false, questionCount = 5) => {
    try {
      const startHizb = reverseMode ? Math.max(1, RANGE_LIMITS.hizb - level + 1) : 1;
      const endHizb = reverseMode ? RANGE_LIMITS.hizb : Math.min(RANGE_LIMITS.hizb, level);

      return await buildExamFromUnits({
        unitType: 'hizb',
        units: buildOrderedRange(startHizb, endHizb, reverseMode),
        questionCount,
        translationEdition,
        reverseMode,
        rangeLabel: `${startHizb}-${endHizb}`
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des versets d\'examen:', error);
      throw error;
    }
  },

  // Récupérer un verset aléatoire dans une plage de pages, de juz, de hizbs ou de sourates
  getRandomVerseByRange: async (rangeType, startRange, endRange, translationEdition = 'fr.hamidullah', reverseMode = false) => {
    const MAX_ATTEMPTS = 8;
    const units = buildOrderedRange(startRange, endRange, reverseMode);

    if (units.length === 0) {
      throw new Error('Plage de révision invalide');
    }

    // Chaque tentative repart sur une unité différente pour éviter de boucler sur un trou
    const candidates = shuffleArray(units).slice(0, MAX_ATTEMPTS);

    for (const unitValue of candidates) {
      try {
        const ayahs = await getAyahsForUnit(rangeType, unitValue);
        if (!ayahs.length) {
          continue;
        }

        const selectedAyah = ayahs[Math.floor(Math.random() * ayahs.length)];
        const verse = await QuranAPI.getVerseWithTranslation(
          selectedAyah.surah.number,
          selectedAyah.numberInSurah,
          translationEdition
        );

        if (verse?.error) {
          // Réponse dégradée de l'API (pas de texte arabe) : essayer une autre unité
          continue;
        }

        verse.examInfo = {
          selectionType: rangeType,
          selectionRange: `${Math.min(startRange, endRange)}-${Math.max(startRange, endRange)}`,
          selectedValue: unitValue
        };

        return verse;
      } catch (error) {
        console.error(`Erreur lors de la récupération de ${rangeType} ${unitValue}:`, error);
      }
    }

    throw new Error(`Impossible de trouver un verset valide après ${candidates.length} tentatives`);
  },

  // Examen sur une plage de pages, de juz, de hizbs ou de sourates.
  // Le nombre de questions est réparti uniformément sur toute la plage choisie :
  //  - plage large (ex. 10 pages / 5 questions) : une question par segment de 2 pages,
  //  - plage étroite (ex. 2 pages / 5 questions) : un quota équilibré page par page.
  getExamVersesByRange: async (rangeType, startRange, endRange, translationEdition = 'fr.hamidullah', reverseMode = false, questionCount = 5) => {
    try {
      const limit = RANGE_LIMITS[rangeType] || RANGE_LIMITS.page;
      const safeStart = Math.min(Math.max(1, Number(startRange) || 1), limit);
      const safeEnd = Math.min(Math.max(1, Number(endRange) || 1), limit);

      return await buildExamFromUnits({
        unitType: rangeType,
        units: buildOrderedRange(safeStart, safeEnd, reverseMode),
        questionCount,
        translationEdition,
        reverseMode,
        rangeLabel: `${Math.min(safeStart, safeEnd)}-${Math.max(safeStart, safeEnd)}`
      });
    } catch (error) {
      console.error('Erreur lors de la génération de l\'examen par plage:', error);
      throw error;
    }
  },

};

export default QuranAPI;
